// A boat's visits on the Service sheet (features/serviceVisits.ts), opened from a vessel's "Visit 2 of 3" tag:
// every visit of the boat, newest first under its season, with its dates, where the boat is and how many works
// are done. Another visit opens in the Board; with Service write access a new visit can be added from here.
import { InlineError, Modal } from './ui.js';
import { useDateFormatter } from '../lib/format.js';
import type { ServiceVisit } from '../features/serviceVisits.js';

export interface VisitLine extends ServiceVisit {
  /** where the boat is for this visit (the sheet's @ Shipyard: Expected, Yes, Delivered…), '' when unset */
  state: string;
  /** its works, done / all */
  done: number;
  works: number;
}

export function ServiceVisitsDialog({ boat, visits, current, currentSeason, canWrite, adding, error, onShow, onNewVisit, onClose }: {
  boat: string;
  /** every visit of the boat, oldest first */
  visits: readonly VisitLine[];
  /** the visit it was opened from */
  current: number;
  currentSeason: string;
  canWrite: boolean;
  adding: boolean;
  /** why the last New visit failed, shown here (the page's own message sits behind the dialog) */
  error?: unknown;
  onShow: (id: number) => void;
  onNewVisit: () => void;
  onClose: () => void;
}) {
  const df = useDateFormatter();
  const day = (iso: string) => df.date(iso);
  const seasons: Array<{ season: string; list: VisitLine[] }> = [];
  for (const v of [...visits].reverse()) {
    const at = seasons.find((s) => s.season === v.season);
    if (at) at.list.push(v);
    else seasons.push({ season: v.season, list: [v] });
  }
  const name = visits.find((v) => v.id === current)?.name ?? boat;
  return (
    <Modal
      title={`Visits · ${name}`}
      dataTitle
      width={520}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
          {canWrite && (
            <button type="button" className="btn btn-primary" disabled={adding} onClick={onNewVisit} title="Register this boat's next visit">
              {adding ? 'Adding…' : 'New visit'}
            </button>
          )}
        </>
      }
    >
      {error != null && <InlineError error={error} />}
      <p className="muted svc-visits-lead">
        {visits.length === 1 ? 'One visit' : `${visits.length} visits`} of this boat on the Service sheet. Each visit keeps its own works, dates, forms, photos and wash.
      </p>
      {seasons.map(({ season, list }) => (
        <section key={season || 'none'} className="svc-visits-season" aria-label={season ? `Season ${season}` : 'No season'}>
          <h4 className="svc-visits-hd">
            {season ? `Season ${season}` : 'No season'}
            {season === currentSeason && <span className="svc-visits-cur"> · this season</span>}
            {list.length > 1 && <span className="muted"> · {list.length} visits</span>}
          </h4>
          <ul className="svc-visits">
            {list.map((v) => {
              const here = v.id === current;
              const dates = !v.from ? 'No dates yet' : v.from === v.to ? day(v.from) : `${day(v.from)} → ${day(v.to)}`;
              return (
                <li key={v.id} className="svc-visit-row" aria-current={here ? 'true' : undefined}>
                  <span className="svc-visit-main">
                    <span className="svc-visit-t">
                      {v.of > 1 ? `Visit ${v.n} of ${v.of}` : 'Only visit'}
                      {v.name !== name && <span className="svc-visit-as"> · as {v.name}</span>}
                    </span>
                    <span className="svc-visit-d">
                      {[dates, v.state, v.works ? `${v.done}/${v.works} works done` : 'No works'].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {here
                    ? <span className="svc-visit-this">This visit</span>
                    : <button type="button" className="btn btn-sm" onClick={() => onShow(v.id)} aria-label={`Show ${v.of > 1 ? `visit ${v.n} of ${v.of}` : 'the visit'} of season ${v.season || 'unknown'}`}>Show</button>}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </Modal>
  );
}
